import { useTranslation } from 'react-i18next';
import type { IApiBuyerProfile, IApiSearchCardCondition } from '@/services/swagger/easourcing/models';

// ─── Props ────────────────────────────────────────────────────

type ProcurementRequestConfirmationCardProps = {
  condition: IApiSearchCardCondition;
  buyerProfile?: IApiBuyerProfile | null;
  className?: string;
};

// ─── 字段行 ────────────────────────────────────────────────────

type FieldRowProps = {
  label: string;
  value?: string | null;
};

function FieldRow({ label, value }: FieldRowProps) {
  if (!value) return null;
  return (
    <div className="flex gap-[10px] w-full">
      <span className="shrink-0 text-[#525252]">{label}</span>
      <span className="font-medium text-[#09090B]">{value}</span>
    </div>
  );
}

// ─── 主组件 ──────────────────────────────────────────────────

export function ProcurementRequestConfirmationCard({
  condition,
  buyerProfile,
  className,
}: ProcurementRequestConfirmationCardProps) {
  const { t } = useTranslation();

  const { queryProduct, quantity, quantityUnit, unitPrice, unitCurrency, country, details } = condition;

  const quantityDisplay = [quantity, quantityUnit].filter(Boolean).join(' ');
  const unitPriceDisplay = unitCurrency && unitPrice ? `${unitCurrency}${unitPrice}` : (unitPrice ?? null);
  const targetMarketDisplay = country?.length ? country.join(', ') : null;
  const companyDisplay = buyerProfile?.companyName ?? null;

  return (
    <div
      className={['flex flex-col gap-[10px] items-stretch w-full', 'bg-[#F8F8F7] rounded-lg p-5', className]
        .filter(Boolean)
        .join(' ')}
    >
      {/* 标题 */}
      <p className="w-full text-base font-bold leading-[150%] text-[#09090B]">
        {t('chat.searchCard.procurementRequestConfirmation', 'Procurement Request Confirmation')}
      </p>

      {/* 字段列表 */}
      <FieldRow label={t('chat.searchCard.productName', 'Product name:')} value={queryProduct} />
      <FieldRow label={t('chat.searchCard.purchaseQuantity', 'Purchase quantity:')} value={quantityDisplay || null} />
      <FieldRow label={t('chat.searchCard.unitPrice', 'Unit price:')} value={unitPriceDisplay} />
      <FieldRow label={t('chat.searchCard.targetMarket', 'Target market:')} value={targetMarketDisplay} />
      <FieldRow label={t('chat.searchCard.details', 'Details:')} value={details} />
      <FieldRow label={t('chat.searchCard.company', 'Company:')} value={companyDisplay} />

      {/* 底部确认横幅 */}
      <div className="flex flex-row items-center  gap-[10px] w-full bg-[#E1F4ED] rounded-lg px-3 py-[12px]">
        <span className="shrink-0 text-base leading-[150%]" role="img" aria-label="confirmed">
          ✅
        </span>
        <p className="text-base leading-[150%] text-[#064E3B]">
          {t(
            'chat.searchCard.requestClearMsg',
            'Your request is clear. We are now sourcing globally to find you the best match.'
          )}
        </p>
      </div>
    </div>
  );
}
